import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LOGO_URL, NAV_LINKS } from "@/lib/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-ink/85 backdrop-blur-md border-b border-hairline"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-brand"
          className="flex items-center gap-4 group"
        >
          <img
            src={LOGO_URL}
            alt="KP Capital crest"
            className="h-16 w-16 md:h-20 md:w-20 object-contain"
          />
          <div className="leading-none">
            <div className="font-serif text-lg tracking-[0.18em] text-bone">
              KP&nbsp;·&nbsp;CAPITAL
            </div>
            <div className="text-[10px] tracking-[0.3em] text-smoke uppercase mt-1">
              Private Trading Society
            </div>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {NAV_LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-xs tracking-[0.25em] uppercase text-sand hover:text-platinum transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <a
          href="#apply"
          data-testid="nav-apply-cta"
          className="hidden md:inline-flex items-center gap-3 border border-hairline px-5 py-3 text-[10px] tracking-[0.3em] uppercase text-bone hover:bg-elevated hover:border-platinum transition-colors"
        >
          Apply <span aria-hidden>→</span>
        </a>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
          className="md:hidden inline-flex flex-col gap-1.5 p-2"
        >
          <span className={`h-px w-6 bg-bone transition-transform ${open ? "translate-y-[7px] rotate-45" : ""}`} />
          <span className={`h-px w-6 bg-bone transition-opacity ${open ? "opacity-0" : ""}`} />
          <span className={`h-px w-6 bg-bone transition-transform ${open ? "-translate-y-[7px] -rotate-45" : ""}`} />
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-hairline bg-ink/95 backdrop-blur-md" data-testid="nav-mobile-panel">
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-sm tracking-[0.25em] uppercase text-sand hover:text-platinum transition-colors"
              >
                {l.label}
              </a>
            ))}
            <a
              href="#apply"
              onClick={() => setOpen(false)}
              className="border border-hairline px-5 py-3 text-xs tracking-[0.3em] uppercase text-bone text-center"
            >
              Apply
            </a>
          </div>
        </div>
      )}
    </motion.header>
  );
}
